import { getAssistantSurface, type MapSurfaceHandle } from './assistant-actions'
import type { ExecuteClientToolDeps } from './client-tool-types'
import { readLayerOperations } from './read-layer-operations'
import { readMapSettings } from './read-map-settings'
import { readStyleRequest } from './read-style-request'

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

/** Map tools need a registered map surface; off `/map` there is nothing to act on. */
const NO_MAP = { error: 'There is no map on this page.' } as const

/**
 * How long a map tool waits for the map surface to register before giving up.
 *
 * `navigate_to` settles as soon as the ROUTE STRING changes (200ms deadline),
 * but `/map` then dynamically imports OpenLayers/ArcGIS and awaits
 * `engine.initialize()` — far longer. `useAssistantMapSurface` deliberately
 * registers only once the engine is ready, because a handle registered earlier
 * would accept calls and silently do nothing.
 *
 * Those two facts together produced a real failure: the model navigated to
 * `/map`, immediately called `add_layer_to_map`, found no surface, and reported
 * "there is no map on this page" while the map was still mounting — then
 * invented a plausible-but-wrong explanation for its own race.
 *
 * So the tools WAIT for readiness rather than refusing instantly. This costs
 * nothing on the normal path (the surface is already there, first poll wins)
 * and only elapses in full when a page genuinely has no map — which the
 * server-side `availableActions` narrowing already makes near-unreachable,
 * since the map tools are not offered where no surface is registered.
 */
const SURFACE_WAIT_DEADLINE_MS = 8000
const SURFACE_WAIT_POLL_MS = 50

/**
 * Resolve the MAP surface specifically, waiting briefly for it to appear.
 *
 * A registered dashboard surface does NOT satisfy this wait — it keeps
 * polling, exactly as if nothing had registered yet, because a map tool
 * resolved against a dashboard handle would reach for `surface.actions` that
 * don't exist. Returns null only if no MAP surface registered within the
 * deadline — the honest "there really is no map here" case (which is also
 * true when the registered surface is a dashboard).
 */
async function awaitAssistantSurface(): Promise<MapSurfaceHandle | null> {
  const deadline = Date.now() + SURFACE_WAIT_DEADLINE_MS

  for (;;) {
    const surface = getAssistantSurface()
    if (surface && surface.kind === 'map') return surface
    if (Date.now() >= deadline) return null
    await delay(SURFACE_WAIT_POLL_MS)
  }
}

function readLayerId(input: unknown): string | null {
  const raw = (input ?? {}) as { layer_id?: unknown }

  return typeof raw.layer_id === 'string' && raw.layer_id.trim() !== '' ? raw.layer_id.trim() : null
}

export async function executeAddLayers(input: unknown): Promise<unknown> {
  const surface = await awaitAssistantSurface()
  if (!surface) return NO_MAP

  const raw = (input ?? {}) as { layer_ids?: unknown }

  if (!Array.isArray(raw.layer_ids) || raw.layer_ids.some(id => typeof id !== 'string')) {
    return { error: '`layer_ids` must be an array of layer IDs.' }
  }

  // The whole batch goes in ONE call: "add these five layers" is one user
  // intent, and five separate continuations would trip the run budget.
  return { results: await surface.actions.addLayer(raw.layer_ids as string[]) }
}

export async function executeToggleLayer(input: unknown): Promise<unknown> {
  const surface = await awaitAssistantSurface()
  if (!surface) return NO_MAP

  const layerId = readLayerId(input)
  if (!layerId) return { error: '`layer_id` must be a layer ID.' }

  const raw = (input ?? {}) as { visible?: unknown }
  if (typeof raw.visible !== 'boolean') return { error: '`visible` must be true or false.' }

  return await surface.actions.toggleLayer(layerId, raw.visible)
}

export async function executeRemoveLayer(input: unknown): Promise<unknown> {
  const surface = await awaitAssistantSurface()
  if (!surface) return NO_MAP

  const layerId = readLayerId(input)
  if (!layerId) return { error: '`layer_id` must be a layer ID.' }

  return await surface.actions.removeLayer(layerId)
}

export async function executeZoomToLayer(input: unknown): Promise<unknown> {
  const surface = await awaitAssistantSurface()
  if (!surface) return NO_MAP

  const layerId = readLayerId(input)
  if (!layerId) return { error: '`layer_id` must be a layer ID.' }

  return await surface.actions.zoomToLayer(layerId)
}

/**
 * Read a `[lng, lat]` pair the model authored. Both entries must be finite
 * numbers — a NaN would silently move the map nowhere and still report a move.
 */
function readCenter(input: unknown): [number, number] | null {
  const raw = (input ?? {}) as { center?: unknown }
  if (!Array.isArray(raw.center) || raw.center.length !== 2) return null
  const [lng, lat] = raw.center
  if (typeof lng !== 'number' || !Number.isFinite(lng)) return null
  if (typeof lat !== 'number' || !Number.isFinite(lat)) return null

  return [lng, lat]
}

export async function executeZoomToCoordinates(input: unknown): Promise<unknown> {
  const surface = await awaitAssistantSurface()
  if (!surface) return NO_MAP

  const center = readCenter(input)
  if (!center) return { error: '`center` must be a [longitude, latitude] pair of numbers.' }

  const raw = (input ?? {}) as { zoom?: unknown }
  const zoom = typeof raw.zoom === 'number' && Number.isFinite(raw.zoom) ? raw.zoom : undefined

  return await surface.actions.zoomToCoordinates(center, zoom)
}

export async function executeUpdateMapLayers(input: unknown): Promise<unknown> {
  const surface = await awaitAssistantSurface()
  if (!surface) return NO_MAP
  const operations = readLayerOperations(input)
  if (!operations) return { error: '`operations` must be a list of 1 to 20 operations.' }

  return await surface.actions.updateMapLayers(operations)
}

export async function executeListBasemaps(): Promise<unknown> {
  const surface = await awaitAssistantSurface()
  if (!surface) return NO_MAP

  return await surface.actions.listBasemaps()
}

export async function executeSetBasemap(input: unknown): Promise<unknown> {
  const surface = await awaitAssistantSurface()
  if (!surface) return NO_MAP
  const raw = (input ?? {}) as { basemap_id?: unknown }

  if (typeof raw.basemap_id !== 'string' || !raw.basemap_id.trim()) {
    return { error: '`basemap_id` must be a basemap id from list_basemaps.' }
  }

  return await surface.actions.setBasemap(raw.basemap_id.trim())
}

export async function executeStyleLayer(input: unknown): Promise<unknown> {
  const surface = await awaitAssistantSurface()
  if (!surface) return NO_MAP
  const request = readStyleRequest(input)
  if (!request) return { error: '`layer_id` must be a layer id from the map context.' }

  return await surface.actions.styleLayer(request)
}

export async function executeSetMapSettings(input: unknown): Promise<unknown> {
  const surface = await awaitAssistantSurface()
  if (!surface) return NO_MAP
  const request = readMapSettings(input)
  if (!request) return { error: 'Pass at least one of engine, view_type, widgets or environment.' }

  return await surface.actions.setMapSettings(request)
}

export async function executeSaveMap(input: unknown, deps: ExecuteClientToolDeps): Promise<unknown> {
  const surface = await awaitAssistantSurface()
  if (!surface) return NO_MAP
  const raw = (input ?? {}) as { mode?: unknown; name?: unknown }
  if (raw.mode !== 'save' && raw.mode !== 'save_as') return { error: '`mode` must be "save" or "save_as".' }

  return await surface.actions.saveMap(
    { mode: raw.mode, ...(typeof raw.name === 'string' ? { name: raw.name } : {}) },
    deps.confirm,
  )
}
